import { ArrowRightFilledIcon, GotoBlackYellowIcon, GotoWhiteYellowIcon } from '@getgo/chameleon-icons/react'
import { Button, Typography } from '@getgo/chameleon-web-react-wrapper'
import { Box } from '@material-ui/core'

import { GTATourFeatureDot, GTATourLengthByRole } from 'utils/constants'
import FeatureDot from './FeatureDot'
import { GotoAdminTourData } from './GotoAdminTour'
const getGotoAdminTourData = (
  t: Function,
  classes: object,
  language: string,
  theme: string,
  screenNumber: number,
  setScreenNumber: (screenNumber: number) => void,
  isBillingAdmin: boolean,
): GotoAdminTourData => {
  const yourUsersList = [t('Change settings'), t('Add or remove products'), t('Change roles')]

  const accountSettingsList = [
    t('Settings templates'),
    t('Email templates'),
    t('Account-wide permissions for your products'),
  ]
  // FeatureDot component length is one lesser since first screen of the tour doesnt have FeatureDot
  const featureDotLength = isBillingAdmin
    ? GTATourLengthByRole.PRODUCT_BILLING_ADMIN_TOUR_MAX_LENGTH - 1
    : GTATourLengthByRole.PRODUCT_ADMIN_TOUR_MAX_LENGTH - 1
  const data = [
    {
      title: t('The new admin experience is here!'),
      message: (
        <>
          <Box className={classes['gotoAdminTourIcons']}>
            {theme === 'light' ? <GotoBlackYellowIcon size="60px" /> : <GotoWhiteYellowIcon size="60px" />}
            <Typography variant="body-large-strong" color="type-color-default">
              {t('Admin Center')}
            </Typography>
            <ArrowRightFilledIcon />
            {theme === 'light' ? <GotoBlackYellowIcon size="60px" /> : <GotoWhiteYellowIcon size="60px" />}
            <Typography variant="body-large-strong" color="type-color-default">
              {t('Admin')}
            </Typography>
          </Box>
          <Typography variant="body-medium" color="type-color-default">
            {t(
              'Your account was transitioned to the new GoTo Admin experience by a super admin. Here you’ll find all the features you’re used to – things have just moved around a little.',
            )}
          </Typography>
          <Typography variant="body-medium" color="type-color-default">
            {t('Let us show you around!')}
          </Typography>
        </>
      ),
      cta: (
        <Box className={classes['gotoAdminTourCtas']}>
          <Button
            size="medium"
            variant="secondary"
            data-testid="explore-btn"
            onClick={() => {
              setScreenNumber(screenNumber - 1)
            }}
          >
            {t('Explore on my own')}
          </Button>
          <Button
            size="medium"
            variant="primary"
            onClick={() => {
              setScreenNumber(screenNumber + 1)
            }}
            data-testid="tour-btn"
          >
            {t('Take a quick tour')}
          </Button>
        </Box>
      ),
    },
    {
      title: t('Product home page'),
      message: (
        <>
          <Box className={classes['gotoAdminTourImage']}>
            <img
              src={require(`../../assets/gotoAdminTourImages/productHomePage/productHomePage_${language}.png`)}
              alt="product home page"
              width="360px"
            />
          </Box>
          <Typography variant="body-medium" color="type-color-default">
            {t('From the home page, you can find information about your products and licenses at a glance.')}
          </Typography>
          <Typography
            variant="body-medium"
            color="type-color-default"
            dangerouslySetInnerHTML={{
              __html: t('Under <b>reports</b>, set up and track reports around admin activity.'),
            }}
          />
        </>
      ),
      cta: (
        <Box className={classes['gotoAdminTourCtas']}>
          <Box className={classes['gotoAdminTourDots']}>
            {Array.from({ length: featureDotLength }).map((_, index) => (
              <FeatureDot key={`first+${index}`} isActive={index === GTATourFeatureDot.FIRST_SCREEN_ACTIVE} />
            ))}
          </Box>
          <Button
            size="medium"
            variant="tertiary"
            onClick={() => setScreenNumber(screenNumber - 1)}
            data-testid="first-slide-back-btn"
          >
            {t('Back')}
          </Button>
          <Button
            size="medium"
            variant="primary"
            onClick={() => setScreenNumber(screenNumber + 1)}
            trailingIcon={<ArrowRightFilledIcon />}
            data-testid="first-slide-next-btn"
          >
            {t('Next')}
          </Button>
        </Box>
      ),
    },
    {
      title: t('Your users'),
      message: (
        <>
          <Box className={classes['gotoAdminTourImage']}>
            <img
              src={require(`../../assets/gotoAdminTourImages/yourUsers/yourUsers_${language}.png`)}
              alt="users"
              width="360px"
            />
          </Box>
          <Typography
            variant="body-medium"
            color="type-color-default"
            dangerouslySetInnerHTML={{
              __html: t('Under <b>people</b> is where you can add, delete, and edit users individually or in bulk.'),
            }}
          />
          <Typography variant="body-medium" color="type-color-default">
            {t('Here are just a few of the changes you can make right from the actions menu:')}
          </Typography>
          <ul>
            {yourUsersList.map((desc, index) => (
              <li>
                <Typography key={`your-users-list-${index}`} variant="body-medium" color="type-color-default">
                  {desc}
                </Typography>
              </li>
            ))}
          </ul>
        </>
      ),
      cta: (
        <Box className={classes['gotoAdminTourCtas']}>
          <Box className={classes['gotoAdminTourDots']}>
            {Array.from({ length: featureDotLength }).map((_, index) => (
              <FeatureDot key={`first+${index}`} isActive={index === GTATourFeatureDot.SECOND_SCREEN_ACTIVE} />
            ))}
          </Box>
          <Button
            size="medium"
            variant="tertiary"
            onClick={() => setScreenNumber(screenNumber - 1)}
            data-testid="second-slide-back-btn"
          >
            {t('Back')}
          </Button>
          <Button
            size="medium"
            variant="primary"
            onClick={() => setScreenNumber(screenNumber + 1)}
            trailingIcon={<ArrowRightFilledIcon />}
            data-testid="second-slide-next-btn"
          >
            {t('Next')}
          </Button>
        </Box>
      ),
    },
    {
      title: t('Account settings'),
      message: (
        <>
          <Box className={classes['gotoAdminTourImage']}>
            <img
              src={require(`../../assets/gotoAdminTourImages/accountSettings/accountSettings_${language}.png`)}
              alt="account settings"
              width="360px"
            />
          </Box>
          <Typography
            variant="body-medium"
            color="type-color-default"
            dangerouslySetInnerHTML={{
              __html: t(
                'Go to the <b>settings</b> menu to adjust account-level permissions. This is also where you can create and maintain:',
              ),
            }}
          />
          <ul>
            {accountSettingsList.map((desc, index) => (
              <li>
                <Typography key={`account-settings-list-${index}`} variant="body-medium" color="type-color-default">
                  {desc}
                </Typography>
              </li>
            ))}
          </ul>
        </>
      ),
      cta: (
        <Box className={classes['gotoAdminTourCtas']}>
          <Box className={classes['gotoAdminTourDots']}>
            {Array.from({ length: featureDotLength }).map((_, index) => (
              <FeatureDot
                key={`first+${index}`}
                isActive={
                  isBillingAdmin
                    ? index == GTATourFeatureDot.FOURTH_SCREEN_ACTIVE
                    : index == GTATourFeatureDot.THIRD_SCREEN_ACTIVE
                }
              />
            ))}
          </Box>
          <Button
            size="medium"
            variant="tertiary"
            onClick={() => setScreenNumber(screenNumber - 1)}
            data-testid="third-slide-back-btn"
          >
            {t('Back')}
          </Button>
          <Button
            size="medium"
            variant="primary"
            onClick={() => setScreenNumber(screenNumber + 1)}
            trailingIcon={<ArrowRightFilledIcon />}
            data-testid="third-slide-next-btn"
          >
            {t('Next')}
          </Button>
        </Box>
      ),
    },
    {
      title: t('Discover everything GoTo Admin has to offer'),
      message: (
        <>
          <Box className={classes['gotoAdminTourImage']}>
            <img
              src={require(`../../assets/gotoAdminTourImages/discoverEverything/discoverEverything_${language}.png`)}
              alt="discover goto dmin"
              width="360px"
            />
          </Box>
          <Typography
            variant="body-medium"
            color="type-color-default"
            dangerouslySetInnerHTML={{
              __html: t(
                'Want to dive deeper? Access tutorials and training videos for GoTo Admin under the <b>help</b> menu.',
              ),
            }}
          />
          <Typography variant="body-medium" color="type-color-default">
            {t('You’ll also find access to our support site there.')}
          </Typography>
        </>
      ),
      cta: (
        <Box className={classes['gotoAdminTourCtas']}>
          <Box className={classes['gotoAdminTourDots']}>
            {Array.from({ length: featureDotLength }).map((_, index) => (
              <FeatureDot
                key={`first+${index}`}
                isActive={
                  isBillingAdmin
                    ? index == GTATourFeatureDot.FIFTH_SCREEN_ACTIVE
                    : index == GTATourFeatureDot.FOURTH_SCREEN_ACTIVE
                }
              />
            ))}
          </Box>
          <Button
            size="medium"
            variant="tertiary"
            onClick={() => setScreenNumber(screenNumber - 1)}
            data-testid="fourth-slide-back-btn"
          >
            {t('Back')}
          </Button>
          <Button
            size="medium"
            variant="primary"
            onClick={() => setScreenNumber(screenNumber + 1)}
            data-testid="done-btn"
          >
            {t('Done')}
          </Button>
        </Box>
      ),
    },
  ]
  isBillingAdmin &&
    data.splice(3, 0, {
      title: t('Everything around billing'),
      message: (
        <>
          <Box className={classes['gotoAdminTourImage']}>
            <img
              src={require(`../../assets/gotoAdminTourImages/billing/billing_${language}.png`)}
              alt="billing tab"
              width="360px"
            />
          </Box>
          <Typography
            variant="body-medium"
            color="type-color-default"
            dangerouslySetInnerHTML={{
              __html: t(
                'Here you’ll find all info around <b>billing</b> and your subscriptions. Only people with a billing admin role can access this area.',
              ),
            }}
          />
        </>
      ),
      cta: (
        <Box className={classes['gotoAdminTourCtas']}>
          <Box className={classes['gotoAdminTourDots']}>
            {Array.from({ length: featureDotLength }).map((_, index) => (
              <FeatureDot key={`first+${index}`} isActive={index == GTATourFeatureDot.THIRD_SCREEN_ACTIVE} />
            ))}
          </Box>
          <Button
            size="medium"
            variant="tertiary"
            data-testid="billing-slide-back-btn"
            onClick={() => {
              setScreenNumber(screenNumber - 1)
            }}
          >
            {t('Back')}
          </Button>
          <Button
            size="medium"
            trailingIcon={<ArrowRightFilledIcon />}
            data-testid="billing-slide-next-btn"
            onClick={() => {
              setScreenNumber(screenNumber + 1)
            }}
          >
            {t('Next')}
          </Button>
        </Box>
      ),
    })

  return data[screenNumber]
}

export default getGotoAdminTourData
